// React
import React, { useState, useEffect, useCallback } from 'react';

// Assets
import { RightOutlined } from '@ant-design/icons';

// Packages
import {
    Stack,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    OutlinedInput,
    Button
} from '@mui/material';

// Utils
import { drawerTypes, userRoles } from 'utils/constants';
import { isStaffEmail } from 'utils/helpers/email.helper';

const CallSearch = ({
    residents,
    getPropertyMembers,
    getPropertyMembersCodes,
    setPropertyAddress,
    changingFocus,
    setDrawerTitle,
    setDrawerType,
    isActive,
    getVgPropertyById,
    activeOrg,
    openGuardLog
}) => {
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [input, setSearchInput] = useState('');
    const [searchList, setSearchList] = useState([]);

    const formatSearchList = useCallback(({ residents, activeOrg, setSearchList }) => {
        const formattedList = [];
        const shortCuts = ['Community', 'Sales', 'Golf', 'Country Club', 'Org Name'];
        // Sage staff (ramco.io, ramcoprotective.com) are not listed on the org property
        const isHiddenStaff = (email, propertyId) =>
            propertyId === activeOrg.property_id && isStaffEmail(email);
        const operators = (activeOrg?.users || []).filter(op => !isStaffEmail(op.email));

        const formatShortcuts = shortCuts.map(sc => ({
            active: true,
            first_name: sc === 'Org Name' ? '' : activeOrg.org_name,
            last_name: sc === 'Org Name' ? activeOrg.org_name : sc,
            properties: {
                [activeOrg.property_id]: {
                    address: activeOrg?.address?.address_2
                        ? `${activeOrg?.address.address_1} ${activeOrg?.address.address_2}`
                        : activeOrg?.address.address_1,
                    id: activeOrg.property_id,
                    street_num: activeOrg?.address.address_1.split(/[^\d]+/)[0] || ''
                }
            },
            phone: activeOrg?.phone?.number || '',
            role: 'operator',
            uid: sc,
            pending: false
        }));

        const formatOperators = operators.map(op => ({
            ...op,
            properties: {
                [activeOrg.property_id]: {
                    address: activeOrg?.address?.address_2
                        ? `${activeOrg?.address.address_1} ${activeOrg?.address.address_2}`
                        : activeOrg?.address.address_1,
                    id: activeOrg.property_id,
                    street_num: activeOrg?.address.address_1.split(/[^\d]+/)[0] || ''
                }
            },
            phone: op.phone?.number || '',
            uid: op.id,
            pending: false
        }));

        [...formatShortcuts, ...formatOperators, ...residents].map(resident => {
            const properties = resident.properties
                ? Object.values(resident.properties)
                : null;

            const isInvite =
                !!resident.invite_id ||
                !!resident.invited_at ||
                resident.status === 'invited';

            if (isInvite) {
                if (isHiddenStaff(resident.email, resident.property_id)) {
                    return resident;
                }
                // Unified `invites` rows carry `property_address` (string) and no
                // nested `address` object; legacy guest_invite rows carry an
                // `address` object. Support both.
                const address = resident.property_address
                    ? resident.property_address
                    : resident.address?.address_2
                    ? `${resident.address.address_1} ${resident.address.address_2}`
                    : resident.address?.address_1 || '';
                const propertyId = resident.property_id;
                const streetNum = (address.match(/\d+/) || [''])[0];
                formattedList.push({
                    first_name: resident.first_name,
                    last_name: resident.last_name,
                    check_in_date: resident.check_in_date,
                    access_begins: resident.access_begins,
                    active: resident.active,
                    email: resident.email ? resident.email : '',
                    reservation_id: resident.reservation_id,
                    role: resident.role,
                    uid: resident.invite_id || resident.id, // invite ID
                    phone: resident.phone?.number ? resident.phone?.number : '',
                    address: address,
                    validated: resident.validated,
                    property_id: propertyId,
                    properties: {
                        address: address,
                        id: propertyId,
                        street_num: streetNum
                    },
                    pending: true
                });
            } else {
                if (properties) {
                    properties.map(item => {
                        if (isHiddenStaff(resident.email, item.id ?? item.property_id)) {
                            return item;
                        }
                        formattedList.push({
                            first_name: resident.first_name,
                            last_name: resident.last_name,
                            check_in_date: resident.check_in_date,
                            access_begins: resident.access_begins,
                            active: resident.active,
                            reservation_id: resident.reservation_id,
                            role: item.role,
                            uid: resident.uid,
                            phone: resident.phone,
                            address: item.address,
                            validated: resident.validated,
                            property_id: item.id ?? item.property_id,
                            properties: properties,
                            pending: false
                        });
                        return item;
                    });
                }
            }
            return resident;
        });
        setSearchList(formattedList);
    }, []);

    useEffect(() => {
        if (activeOrg) {
            formatSearchList({
                residents,
                activeOrg,
                setSearchList: setSearchList
            });
        }
    }, [residents, setSearchList, formatSearchList, activeOrg]);

    const handleSuggestionClick = ({ member }) => {
        setSearchSuggestions([]);
        setSearchInput('');
        getPropertyMembers({ propertyId: member.property_id });
        setPropertyAddress(member.address);
        getVgPropertyById({ propertyId: member.property_id });
        getPropertyMembersCodes({ propertyId: member.property_id });
    };

    const _onResidentSearch = input => {
        setSearchInput(input);

        const trimmed = input.trim();
        if (trimmed !== '') {
            const lowerInput = trimmed.toLowerCase();
            const uniqueSuggestions = new Map();

            searchList.forEach(
                ({
                    first_name,
                    last_name,
                    check_in_date,
                    access_begins,
                    active,
                    reservation_id,
                    role,
                    uid,
                    phone,
                    address,
                    validated,
                    property_id,
                    properties,
                    pending
                }) => {
                    const firstName = first_name;
                    const lastName = last_name;
                    const fullId = reservation_id || `${uid}_${property_id}`;

                    const matchesLastName =
                        typeof lastName === 'string'
                            ? lastName.toLowerCase().startsWith(lowerInput)
                            : false;

                    const matchesAddress =
                        typeof address === 'string' &&
                        address.toLowerCase().includes(lowerInput);

                    if (
                        (matchesLastName || matchesAddress) &&
                        (active ||
                            role === userRoles.RESIDENT ||
                            role === userRoles.TENANT)
                    ) {
                        if (!uniqueSuggestions.has(fullId)) {
                            uniqueSuggestions.set(fullId, {
                                first_name: firstName,
                                last_name: lastName,
                                check_in_date: check_in_date || null,
                                utc_seconds: access_begins?.seconds || null,
                                id: fullId,
                                phone,
                                address,
                                validated,
                                res: reservation_id ? true : false,
                                properties,
                                property_id,
                                pending,
                                role
                            });
                        }
                    }
                }
            );

            const sorted = Array.from(uniqueSuggestions.values()).sort((a, b) => {
                return a.utc_seconds - b.utc_seconds;
            });

            setSearchSuggestions(sorted);
        } else {
            setSearchSuggestions([]);
        }
    };

    const resetSearch = useCallback(() => {
        setSearchSuggestions([]);
        setSearchInput('');
    }, []);

    useEffect(() => {
        if (changingFocus) {
            resetSearch();
        }
    }, [changingFocus, resetSearch]);

    return (
        <Stack>
            <Stack
                sx={{
                    display: 'flex',
                    justifyContent: 'center'
                }}
            >
                <OutlinedInput
                    inputProps={{
                        sx: {
                            height: '3vh',
                            backgroundColor: 'rgba(249, 249, 250, 1)',
                            borderRadius: '14px'
                        }
                    }}
                    type="search"
                    placeholder="Search by address or last name"
                    onChange={e => _onResidentSearch(e.target.value)}
                    value={input}
                />
            </Stack>
            <List
                sx={{
                    width: '100%',
                    overflowY: 'auto',
                    marginTop: '2.5vh',
                    maxHeight: '66.75vh'
                }}
            >
                {searchSuggestions.map(member => (
                    <ListItem
                        key={member.id}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            minHeight: '6.5vh',
                            width: '100%',
                            backgroundColor: '#ffffff',
                            borderRadius: '4px',
                            border: '1px solid rgba(243, 243, 243, 1)',
                            paddingX: '4%',
                            paddingY: 0
                        }}
                        button={true}
                        disableGutters
                        onClick={() => {
                            handleSuggestionClick({ member });
                        }}
                    >
                        <>
                            <ListItemText
                                component={'span'}
                                sx={{
                                    maxWidth: '80%!important',
                                    left: '4%'
                                }}
                                primary={
                                    <Typography
                                        sx={{ display: 'block' }}
                                        component="span"
                                        variant="body1"
                                        color="text.primary"
                                    >
                                        {member.address}
                                    </Typography>
                                }
                                secondary={
                                    <Stack flexDirection={'row'} component="span">
                                        <Typography
                                            sx={{
                                                display: 'block',
                                                textTransform: 'capitalize'
                                            }}
                                            variant="body2"
                                            component="span"
                                            color="text.secondary"
                                        >
                                            {`${member.first_name} ${member.last_name}`}
                                        </Typography>
                                        {member.pending && (
                                            <Stack
                                                component="span"
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    backgroundColor:
                                                        'rgba(255, 192, 0, .1)',
                                                    borderRadius: '5px',
                                                    marginLeft: 10
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        display: 'block',
                                                        textTransform: 'uppercase'
                                                    }}
                                                    variant="body2"
                                                    component="span"
                                                    color={'rgba(255, 192, 0, 1)'}
                                                >
                                                    {`pending invite`}
                                                </Typography>
                                            </Stack>
                                        )}
                                        {((Array.isArray(member?.properties)
                                            ? member.properties.find(
                                                  p =>
                                                      p?.id === member?.property_id ||
                                                      p?.property_id === member?.property_id
                                              )?.role
                                            : Object.values(
                                                  member?.properties ?? {}
                                              ).find(
                                                  p =>
                                                      p?.id === member?.property_id ||
                                                      p?.property_id === member?.property_id
                                              )?.role) === userRoles.TENANT ||
                                            member?.role === userRoles.TENANT) && (
                                            <Stack
                                                component="span"
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    borderRadius: '5px',
                                                    marginLeft: 10
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        display: 'block',
                                                        textTransform: 'uppercase'
                                                    }}
                                                    variant="body2"
                                                    component="span"
                                                    color="info.main"
                                                    bgcolor="info.lighter"
                                                >
                                                    {`tenant`}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Stack>
                                }
                            />
                            <ListItemIcon edge="end">
                                <RightOutlined
                                    style={{
                                        color: 'rgba(167, 167, 167, 1)'
                                    }}
                                />
                            </ListItemIcon>
                        </>
                    </ListItem>
                ))}
                {searchSuggestions.length === 0 && input.trim() !== '' && (
                    <ListItem
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: '6.5vh',
                            width: '100%',
                            backgroundColor: '#ffffff',
                            borderRadius: '4px',
                            border: '1px solid rgba(243, 243, 243, 1)',
                            paddingX: '4%'
                        }}
                        disableGutters
                    >
                        <ListItemText
                            primary={
                                <Typography
                                    variant="body1"
                                    component="span"
                                    color="text.secondary"
                                    sx={{
                                        display: 'block',
                                        width: '100%',
                                        textAlign: 'center'
                                    }}
                                >
                                    No results found
                                </Typography>
                            }
                        />
                    </ListItem>
                )}
            </List>
            <Stack
                sx={{
                    width: '100%',
                    overflowY: 'auto',
                    marginTop: '2.5vh',
                    maxHeight: '66.75vh'
                }}
            >
                <Button
                    variant="contained"
                    color="primary"
                    sx={{
                        height: '4.5vh',
                        width: '45%',
                        margin: 'auto',
                        borderRadius: 10,
                        cursor: 'pointer',
                        '&:active::after': {
                            borderRadius: 10
                        }
                    }}
                    onClick={() => {
                        setDrawerTitle(drawerTypes.quickAccess.title);
                        openGuardLog({ quick: true });
                    }}
                    disabled={!isActive}
                >
                    Quick Access
                </Button>
            </Stack>
        </Stack>
    );
};

export default CallSearch;
